import React from 'react';
import clsx from 'clsx';
import { styled } from '@components-shared/src/stitches';
import { LOADING_STYLES } from '@components/main-components/skeleton/Skeleton';
import { ChartCard, ChartCardContainer } from './Card';
import { ChartCardContext } from './Context';

export const AvatarsSkeleton = ({
  amount,
  summary = false,
  size,
  minimal = false,
}: {
  amount: number;
  summary?: boolean;
  size: 'mobile' | 'tablet';
  minimal?: boolean;
}) => {
  return (
    <>
      {Array.from({ length: summary ? Math.min(amount, 5) : amount }, (_, i) => {
        return (
          <ChartCard.Collectors.Wrapper key={i}>
            <ChartCard.Collectors.AvatarContainer key={i}>
              <ChartCard.Collectors.ImageWrapper loading>
                <ChartCard.Collectors.Collector summary={summary} />
              </ChartCard.Collectors.ImageWrapper>
            </ChartCard.Collectors.AvatarContainer>
            {size === 'tablet' && minimal && (
              <>
                <Name />
                <Mints />
              </>
            )}
          </ChartCard.Collectors.Wrapper>
        );
      })}
    </>
  );
};

export const ChartCardSkeleton = ({
  size,
  rows = 10,
  minimal,
}: {
  size: 'mobile' | 'tablet';
  rows?: number;
  minimal?: boolean;
}) => {
  const collectorAmount = size === 'tablet' ? 2 : 3;
  return (
    <Container minimal={minimal}>
      {[...Array(rows)].map((_, i) => (
        <ChartCardContext.Provider key={i} value={{ isLoadingSummary: true, minimal }}>
          <ChartCardContainer loading minimal={minimal}>
            <ChartCard.Cover
              image={null}
              onPlayButtonClick={() => null}
              playButtonPrefetchRef={null}
              currentListeningParty={null}
            />
            <ChartCard.InfoAndCollectors minimal={minimal}>
              <ChartCard.Info header="" subheader="" stats={null} />
              {!minimal && (
                <ChartCard.Row>
                  <ChartCard.Collectors
                    summary
                    collectorAmount={collectorAmount}
                    avatars={
                      <AvatarsSkeleton size={size} summary amount={collectorAmount} minimal />
                    }
                  />
                </ChartCard.Row>
              )}
            </ChartCard.InfoAndCollectors>
            <div className={clsx('hidden md:block', minimal && 'md:hidden')}>
              <ChartCard.Button label="Collect Now" href="" />
            </div>
            <ChartCard.Actions staticActions={<Action />} hoverActions={<Action />} />
          </ChartCardContainer>
        </ChartCardContext.Provider>
      ))}
    </Container>
  );
};

const Container = styled('div', {
  display: 'flex', // Use flex to avoid collapsing borders
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',

  variants: {
    minimal: {
      true: {
        '@tablet': {
          // create grid with 50% column width
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gridTemplateRows: 'repeat(5, auto)',
          gridAutoFlow: 'column',
        },
      },
    },
  },
});

const Action = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 32,
  height: 32,
  borderRadius: 9999,
  backgroundColor: '$neutral100',
});

const Name = styled('div', {
  width: '100%',
  height: 12,
  ...LOADING_STYLES,
  borderRadius: '$large',
  marginY: 4,
});

const Mints = styled('div', {
  width: '75%',
  height: 10,
  ...LOADING_STYLES,
  borderRadius: '$medium',
});
